import { useHandler } from "@redotech/react-util/hook";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import SettingsIcon from "@redotech/redo-icons/settings-02.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  UserContext,
  UserContextValue,
} from "@redotech/redo-merchant-app-common/user";
import { SettingsPermissions } from "@redotech/redo-model/permissions";
import { FlowType, Payer, Step } from "@redotech/redo-model/return-flow";
import { isExchangeAgentEnabledForTeam } from "@redotech/redo-model/return-flow/exchange-agent/exchange-agent-utils";
import {
  ReturnOptionMethod,
  StoreCreditType,
} from "@redotech/redo-model/return-flow/return-option";
import { AdvancedExchanges, Team } from "@redotech/redo-model/team";
import { ReturnState } from "@redotech/redo-return-ui/flows/return-flow/return";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { ButtonSize, IconButton } from "@redotech/redo-web/button";
import { CollapseSubsection } from "@redotech/redo-web/card";
import { Checkbox, CheckboxGroup } from "@redotech/redo-web/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@redotech/redo-web/components/ui/dropdown-menu";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import {
  ProductAutocomplete,
  ProductVariantInfo,
} from "@redotech/redo-web/product-autocomplete";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { produce } from "immer";
import { memo, useContext, useMemo, useState } from "react";
import { ExchangeAgentConfiguration } from "./exchange-agent-configuration";
import { useProductAutocomplete } from "./hooks/use-product-autocomplete";
import { RequiredMessage } from "./required-message";
import * as returnCss from "./return.module.css";

export const returnOptionMethods: ReturnOptionMethod["type"][] = [
  ReturnOptionMethod.EXCHANGE,
  ReturnOptionMethod.STORE_CREDIT,
  ReturnOptionMethod.REFUND,
];

function returnOptionMethod(
  value: ReturnOptionMethod["type"] | undefined,
): string | undefined {
  switch (value) {
    case ReturnOptionMethod.STORE_CREDIT:
      return "Store credit";
    case ReturnOptionMethod.EXCHANGE:
      return "Exchange";
    case ReturnOptionMethod.REFUND:
      return "Refund";
  }
  return undefined;
}

type AdjustmentType = "flat" | "percent";
type AdjustmentCategory = "bonus" | "fee";

interface AdjustmentCategoryData {
  category: AdjustmentCategory;
  label: string;
}

const adjustmentCategoryOptions: RedoListItem<AdjustmentCategoryData>[] = [
  {
    type: "text",
    id: "bonus",
    text: "Bonus",
    value: { category: "bonus", label: "Bonus" },
  },
  {
    type: "text",
    id: "fee",
    text: "Fee",
    value: { category: "fee", label: "Fee" },
  },
];

export const AdjustmentElement = memo(function AdjustmentElement({
  adjustmentFlat,
  adjustmentPercent,
  handleAdjustmentFlat,
  handleAdjustmentPercent,
  canEditReturnsSettings,
}: {
  adjustmentFlat: string;
  adjustmentPercent: string;
  handleAdjustmentFlat(value: string): void;
  handleAdjustmentPercent(value: string): void;
  canEditReturnsSettings: boolean;
}) {
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>(() => {
    if (adjustmentPercent !== "0") {
      return "percent";
    }
    return "flat";
  });

  const currentValue =
    adjustmentType === "flat" ? adjustmentFlat : adjustmentPercent;
  const isNegative = parseFloat(currentValue) < 0;
  const adjustmentCategory: AdjustmentCategory = isNegative ? "fee" : "bonus";

  const formatAdjustmentFlat = useHandler((value: string) => {
    handleAdjustmentFlat((+value).toFixed(2));
  });
  const formatAdjustmentPercent = useHandler((value: string) => {
    if (isNaN(parseFloat(value))) {
      handleAdjustmentPercent("0");
    }
  });

  const handleValueChange =
    adjustmentType === "flat" ? handleAdjustmentFlat : handleAdjustmentPercent;
  const formatValue =
    adjustmentType === "flat" ? formatAdjustmentFlat : formatAdjustmentPercent;

  const selectedCategory =
    adjustmentCategoryOptions.find(
      (opt) => opt.value.category === adjustmentCategory,
    ) || adjustmentCategoryOptions[0];

  const handleCategoryChange = useHandler((category: AdjustmentCategory) => {
    const numValue = parseFloat(currentValue);
    if (isNaN(numValue)) return;

    const absValue = Math.abs(numValue);
    const newValue = category === "fee" ? -absValue : absValue;

    if (adjustmentType === "flat") {
      handleAdjustmentFlat(newValue.toFixed(2));
    } else {
      handleAdjustmentPercent(String(newValue));
    }
  });

  const displayValue = Math.abs(parseFloat(currentValue) || 0).toString();

  const handleDisplayValueChange = useHandler((value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      handleValueChange("0");
      return;
    }

    const signedValue =
      adjustmentCategory === "fee" ? -Math.abs(numValue) : Math.abs(numValue);

    if (adjustmentType === "flat") {
      handleAdjustmentFlat(signedValue.toFixed(2));
    } else {
      handleAdjustmentPercent(String(signedValue));
    }
  });

  const currencySymbol = adjustmentType === "flat" ? "$" : "%";

  return (
    <Flex dir="column" gap="md">
      <RedoSingleSelectDropdownInput
        disabled={!canEditReturnsSettings}
        label="Adjustment type"
        options={adjustmentCategoryOptions}
        optionSelected={({ item }) => handleCategoryChange(item.value.category)}
        selectedItem={selectedCategory}
        size="xs"
      />
      <RedoTextInput
        errorText={
          isNaN(parseFloat(displayValue)) ? "Invalid number" : undefined
        }
        fullWidth
        inputTypeHint="number"
        label="Adjustment amount"
        onBlur={() => formatValue(currentValue)}
        setValue={handleDisplayValueChange}
        state={canEditReturnsSettings ? "default" : "readonly"}
        trailingActions={
          canEditReturnsSettings ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <RedoButton
                  hierarchy="tertiary"
                  IconTrailing={ChevronDownIcon}
                  size="xs"
                  text={currencySymbol}
                  type="button"
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" style={{ zIndex: 1000 }}>
                <DropdownMenuItem
                  onClick={(e) => {
                    setAdjustmentType("flat");
                    handleAdjustmentFlat(currentValue);
                  }}
                >
                  $ Fixed dollar amount
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    setAdjustmentType("percent");
                    handleAdjustmentPercent(currentValue);
                  }}
                >
                  % Percentage
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Text fontSize="sm" p="xs" textColor="secondary">
              {currencySymbol}
            </Text>
          )
        }
        value={displayValue}
      />
    </Flex>
  );
});

export const ReturnDetails = memo(function ReturnDetails({
  state,
  setState,
  allSteps,
  step,
  setStateWithId,
  flowType,
}: StepTypeDetailsProps<ReturnState>) {
  const [
    showInternalOnlyExchangeAgentConfiguration,
    setShowInternalOnlyExchangeAgentConfiguration,
  ] = useState(false);

  const user = useContext(UserContext);
  const team = useContext(TeamContext);
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );

  const exchangeAgentConfig = useExchangeAgentOverride({
    team,
    user,
    flowType,
  });

  // @Brendan we should be able to remove this and we should be able to rely on location flow
  const locationOptions = useMemo(() => {
    return [
      { _id: "", name: "Use default location" },
      ...(team?.settings.locations || []).map((location) => ({
        _id: location._id || "",
        name: location.address?.name || "Unnamed location",
      })),
    ];
  }, [team?.settings.locations]);

  const handleFlatShipping = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.flatShipping = value;
        // Flat shipping and label upcharge are mutually exclusive
        if (value) {
          state.labelUpcharge = false;
        }
      }),
    );
  });

  const handleFlatShippingRate = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        state.flatShippingRate = value;
      }),
    );
  });

  const handleMerchantLabelAdjustment = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.labelUpcharge = value;
        // Flat shipping and label upcharge are mutually exclusive
        if (value) {
          state.flatShipping = false;
        }
      }),
    );
  });

  const handleLabelUpchargeType = useHandler(
    (value: Step.LabelUpchargeType) => {
      setState((state) =>
        produce(state, (state) => {
          state.labelUpchargeType = value;
        }),
      );
    },
  );

  const handleLabelUpchargeValue = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        state.labelUpchargeValue = value;
      }),
    );
  });

  const handleCoverPackagePickup = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.coverPackagePickup = value;
      }),
    );
  });

  const handleExchangeAgentDisable = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.exchangeAgentOverrides = {
          ...state.exchangeAgentOverrides,
          disableExchangeAgent: value,
        };
      }),
    );
  });

  const handleVariantRecommendationsDisable = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.exchangeAgentOverrides = {
          ...state.exchangeAgentOverrides,
          disableVariantRecommendations: value,
        };
      }),
    );
  });

  const handleProductRecommendationsDisable = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.exchangeAgentOverrides = {
          ...state.exchangeAgentOverrides,
          disableProductRecommendations: value,
        };
      }),
    );
  });

  // Contexts required to be defined
  if (!team) {
    return null;
  }

  const getOptionForType = (type: ReturnOptionMethod["type"]) => {
    return state.options.find((opt) => opt.type === type);
  };

  const isOptionEnabled = (type: ReturnOptionMethod["type"]) => {
    return !!getOptionForType(type);
  };

  const allReturnTypes: ReturnOptionMethod["type"][] = [
    ReturnOptionMethod.EXCHANGE,
    ReturnOptionMethod.STORE_CREDIT,
    ReturnOptionMethod.REFUND,
  ];

  const hasAtLeastOneEnabled = allReturnTypes.some((type) =>
    isOptionEnabled(type),
  );

  return (
    <>
      {showInternalOnlyExchangeAgentConfiguration && (
        <RedoModal
          isOpen={showInternalOnlyExchangeAgentConfiguration}
          modalSize="lg"
          onModalCloseRequested={() =>
            setShowInternalOnlyExchangeAgentConfiguration(false)
          }
          title="Internal Only Exchange Agent Configuration"
        >
          <ExchangeAgentConfiguration
            allSteps={allSteps}
            canEditSettings={canEditReturnsSettings}
            setStateWithId={setStateWithId}
            step={step}
          />
        </RedoModal>
      )}
      <Flex dir="column" gap="xl">
        {allReturnTypes.map((type, index) => (
          <StateOptionComponent
            canEditReturnsSettings={canEditReturnsSettings}
            isEnabled={isOptionEnabled(type)}
            key={index}
            option={getOptionForType(type)}
            optionType={type}
            setState={setState}
            state={state}
            team={team}
          />
        ))}
      </Flex>
      {!hasAtLeastOneEnabled && (
        <RequiredMessage>Must have at least one option enabled</RequiredMessage>
      )}
      <Divider />
      <RedoCollapseSubsection title="Advanced return options">
        <RedoToggle
          description="Charge customers a flat cost for return shipping labels. Merchant will receive/pay for the difference between actual cost."
          disabled={!canEditReturnsSettings || state.labelUpcharge}
          label="Flat shipping cost"
          setValue={handleFlatShipping}
          size="sm"
          value={state.flatShipping || false}
        />
        {state.flatShipping && (
          <Flex dir="column" gap="xs">
            <Text fontSize="sm" fontWeight="medium" textColor="primary">
              Flat shipping rate
            </Text>
            <RedoTextInput
              IconLeading={() => <Text fontSize="xs">$</Text>}
              setValue={handleFlatShippingRate}
              state={canEditReturnsSettings ? "default" : "readonly"}
              value={state.flatShippingRate || "0"}
            />
          </Flex>
        )}
        <RedoToggle
          description="Adjust the shipping label cost by a fixed amount or percentage. Positive values increase the cost (upcharge), negative values decrease it (discount)."
          disabled={!canEditReturnsSettings || state.flatShipping}
          label="Label cost adjustment"
          setValue={handleMerchantLabelAdjustment}
          size="sm"
          value={state.labelUpcharge || false}
        />
        {state.labelUpcharge && (
          <Flex dir="column" gap="sm">
            <Flex align="center" dir="row" gap="sm">
              <RedoSingleSelectDropdownInput
                disabled={!canEditReturnsSettings}
                options={[
                  {
                    type: "text" as const,
                    id: Step.LabelUpchargeType.FIXED,
                    text: "Fixed amount ($)",
                    value: {
                      type: Step.LabelUpchargeType.FIXED,
                      label: "Fixed amount ($)",
                    },
                  },
                  {
                    type: "text" as const,
                    id: Step.LabelUpchargeType.PERCENTAGE,
                    text: "Percentage (%)",
                    value: {
                      type: Step.LabelUpchargeType.PERCENTAGE,
                      label: "Percentage (%)",
                    },
                  },
                ]}
                optionSelected={({ item }) =>
                  handleLabelUpchargeType(item.value.type)
                }
                selectedItem={
                  state.labelUpchargeType === Step.LabelUpchargeType.PERCENTAGE
                    ? {
                        type: "text" as const,
                        id: Step.LabelUpchargeType.PERCENTAGE,
                        text: "Percentage (%)",
                        value: {
                          type: Step.LabelUpchargeType.PERCENTAGE,
                          label: "Percentage (%)",
                        },
                      }
                    : {
                        type: "text" as const,
                        id: Step.LabelUpchargeType.FIXED,
                        text: "Fixed amount ($)",
                        value: {
                          type: Step.LabelUpchargeType.FIXED,
                          label: "Fixed amount ($)",
                        },
                      }
                }
                size="xs"
              />
              <RedoTextInput
                IconLeading={() => (
                  <Text fontSize="xs">
                    {state.labelUpchargeType ===
                    Step.LabelUpchargeType.PERCENTAGE
                      ? "%"
                      : "$"}
                  </Text>
                )}
                setValue={handleLabelUpchargeValue}
                state={canEditReturnsSettings ? "default" : "readonly"}
                value={state.labelUpchargeValue || "0"}
              />
            </Flex>
            <Text fontSize="xs" textColor="secondary">
              Use negative values to provide a discount on the label cost
            </Text>
          </Flex>
        )}
        {team.settings.pickupEnabled && (
          <RedoToggle
            description={`Merchant covers package pickup costs up to the max amount set in the shipping settings ${team.settings.pickupMaxCoveredAmountEnabled ? `($${team.settings.pickupMaxCoveredAmount?.toFixed(2) ?? "0.00"})` : "(no max set)"}`}
            disabled={!canEditReturnsSettings}
            label="Cover package pickup"
            setValue={handleCoverPackagePickup}
            size="sm"
            value={state.coverPackagePickup || false}
          />
        )}
        {exchangeAgentConfig.exchangeAgentEnabled && (
          <Flex dir="column">
            <Divider />
            <RedoExternalLink
              fontSize="md"
              text="Exchange Agent settings"
              url={exchangeAgentConfig.settingsLink}
            />
            <Flex align="center" dir="row" justify="space-between">
              <RedoToggle
                description="Don't use Exchange Agent for this step"
                disabled={!canEditReturnsSettings}
                label="Disable Exchange Agent"
                setValue={handleExchangeAgentDisable}
                size="sm"
                value={
                  state.exchangeAgentOverrides?.disableExchangeAgent ?? false
                }
              />
              <Flex dir="column" gap="xxs">
                <Flex align="center" dir="row" gap="sm">
                  {exchangeAgentConfig.displayInternalOnly && (
                    <Tooltip title="Internal only Exchange Agent settings">
                      <IconButton
                        disabled={!canEditReturnsSettings}
                        onClick={() =>
                          setShowInternalOnlyExchangeAgentConfiguration(true)
                        }
                        size={ButtonSize.SMALL}
                      >
                        <SettingsIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Flex>
              </Flex>
            </Flex>
            {!state.exchangeAgentOverrides?.disableExchangeAgent && (
              <Flex dir="column">
                <RedoToggle
                  description="Don't recommend variant exchanges for this step"
                  disabled={
                    !canEditReturnsSettings ||
                    !exchangeAgentConfig.variantRecommendationsEnabled
                  }
                  label="Disable variant recommendations"
                  setValue={handleVariantRecommendationsDisable}
                  size="sm"
                  value={
                    state.exchangeAgentOverrides
                      ?.disableVariantRecommendations ?? false
                  }
                />
                {exchangeAgentConfig.productRecommendationsEnabled && (
                  <RedoToggle
                    description="Don't recommend product exchanges for this step"
                    disabled={
                      !canEditReturnsSettings ||
                      !exchangeAgentConfig.productRecommendationsEnabled
                    }
                    label="Disable product recommendations"
                    setValue={handleProductRecommendationsDisable}
                    size="sm"
                    value={
                      state.exchangeAgentOverrides
                        ?.disableProductRecommendations ?? false
                    }
                  />
                )}
                {(!exchangeAgentConfig.variantRecommendationsEnabled ||
                  !exchangeAgentConfig.productRecommendationsEnabled) && (
                  <Flex dir="column" pt="md">
                    {!exchangeAgentConfig.variantRecommendationsEnabled && (
                      <Text fontSize="sm" textOverflow="wrap">
                        Variant recommendations are currently disabled
                      </Text>
                    )}
                    {!exchangeAgentConfig.productRecommendationsEnabled && (
                      <Text fontSize="sm" textOverflow="wrap">
                        Product recommendations are currently disabled
                      </Text>
                    )}
                  </Flex>
                )}
              </Flex>
            )}
          </Flex>
        )}
        <Divider />
        <RedoSingleSelectDropdownInput
          description="Override the destination shipping location for returns from this step."
          disabled={!canEditReturnsSettings}
          label="Return location"
          options={locationOptions.map(
            (loc): RedoListItem<{ _id: string; name: string }> => ({
              type: "text",
              id: loc._id,
              text: loc.name,
              value: loc,
            }),
          )}
          optionSelected={({ item }) => {
            setState((state) =>
              produce(state, (draft) => {
                draft.locationOverride = item.value._id || undefined;
              }),
            );
          }}
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          selectedItem={
            locationOptions
              .map(
                (loc): RedoListItem<{ _id: string; name: string }> => ({
                  type: "text",
                  id: loc._id,
                  text: loc.name,
                  value: loc,
                }),
              )
              .find((item) => item.id === state.locationOverride) || {
              type: "text",
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              id: locationOptions[0]._id,
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              text: locationOptions[0].name,
              value: locationOptions[0],
            }
          }
          size="xs"
        />
      </RedoCollapseSubsection>
    </>
  );
});

const useExchangeAgentOverride = ({
  team,
  user,
  flowType,
}: {
  team: Team | undefined;
  user: UserContextValue | undefined;
  flowType: FlowType;
}): {
  exchangeAgentEnabled: boolean;
  displayInternalOnly?: boolean;
  variantRecommendationsEnabled?: boolean;
  productRecommendationsEnabled?: boolean;
  settingsLink?: string;
} => {
  const {
    exchangeAgentEnabled,
    variantRecommendationsEnabled,
    productRecommendationsEnabled,
  } = useMemo(() => {
    if (!team || flowType !== FlowType.RETURN) {
      return {
        exchangeAgentEnabled: false,
        variantRecommendationsEnabled: false,
        productRecommendationsEnabled: false,
      };
    }
    const exchangeAgentEnabled = isExchangeAgentEnabledForTeam(
      team.settings.returnsAiExchange,
      team.settings.exchangeAgent,
      team,
    );
    const variantRecommendationsEnabled =
      team.settings.exchangeAgent.variantRecommendations.enabled;
    const productRecommendationsEnabled =
      team.settings.exchangeAgent.productRecommendations.enabled;
    return {
      exchangeAgentEnabled,
      variantRecommendationsEnabled,
      productRecommendationsEnabled,
    };
  }, [team, flowType]);

  if (!exchangeAgentEnabled) {
    return { exchangeAgentEnabled };
  }

  return {
    exchangeAgentEnabled,
    displayInternalOnly: !!(user?.email && /@getredo\.com/.test(user?.email)),
    variantRecommendationsEnabled,
    productRecommendationsEnabled,
    settingsLink: `${window.location.href.split("/settings")[0]}/settings/exchanges`,
  };
};

const StateOptionComponent = memo(function StateOptionComponent({
  team,
  state,
  setState,
  canEditReturnsSettings,
  optionType,
  option,
  isEnabled,
}: {
  team: Team;
  state: StepTypeDetailsProps<ReturnState>["state"];
  setState: StepTypeDetailsProps<ReturnState>["setState"];
  canEditReturnsSettings: boolean;
  optionType: ReturnOptionMethod["type"];
  option: ReturnState["options"][number] | undefined;
  isEnabled: boolean;
}) {
  const { productsState, selectedProduct, onSelectedProductChange } =
    useProductAutocomplete(team._id, option?.variantExchangeAddOnVariantId);

  const getIndex = () =>
    state.options.findIndex((opt) => opt.type === optionType);

  const handleToggle = useHandler((enabled: boolean) => {
    setState((state) =>
      produce(state, (draft) => {
        if (enabled) {
          draft.options.push({
            type: optionType,
            creditType: StoreCreditType.GIFT_CARD,
            labelPayer: Payer.CUSTOMER,
            ship: true,
            manual_label: false,
            exchangeTypes: {
              sameItem: true,
              variant: true,
              exchangeGroups: true,
              advanced: true,
            },
            chargeCustomerForNewOrderShipping: false,
            adjustmentFlat: "0",
            adjustmentPercent: "0",
            adjustmentType: "flat",
            refundPointsRedeemedForItem: false,
            deductEarnedPointsFromRefund: false,
            reimburseForFinalSale: true,
          });
        } else {
          const index = draft.options.findIndex(
            (opt) => opt.type === optionType,
          );
          if (index !== -1) {
            draft.options.splice(index, 1);
          }
        }
      }),
    );
  });

  const handleAdjustmentFlat = useHandler((value: string) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].adjustmentFlat = value;
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].adjustmentPercent = "0";
      }),
    );
  });

  const handleAdjustmentPercent = useHandler((value: string) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].adjustmentPercent = value;
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].adjustmentFlat = "0";
      }),
    );
  });

  const handleGreenReturn = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].ship = !value;
      }),
    );
  });

  const handleManualLabel = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].manual_label = value;
        if (value) {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          state.options[index].ship = true;
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          state.options[index].labelPayer = Payer.CUSTOMER;
        }
      }),
    );
  });

  const handleSafetyNet = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].labelPayer = value
          ? Payer.MERCHANT
          : Payer.CUSTOMER;
      }),
    );
  });

  const handleExchangeTypeSameItem = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].exchangeTypes.sameItem = value;
      }),
    );
  });

  const handleExchangeTypeVariant = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].exchangeTypes.variant = value;
      }),
    );
  });

  const handleExchangeTypeExchangeGroups = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].exchangeTypes.exchangeGroups = value;
      }),
    );
  });

  const handleExchangeTypeAdvanced = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].exchangeTypes.advanced = value;
      }),
    );
  });

  const handleVariantExchangeVariantId = useHandler(
    (value: ProductVariantInfo | null | undefined) => {
      const index = getIndex();
      if (index === -1) return;
      onSelectedProductChange(value ?? null);
      if (value) {
        setState((state) =>
          produce(state, (state) => {
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            state.options[index].variantExchangeAddOnVariantId =
              value.variantId;
          }),
        );
      }
    },
  );

  const handleVariantExchangeAddOnTitle = useHandler((value: string) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].variantExchangeAddOnTitle = value;
      }),
    );
  });

  const handleVariantExchangeAddOnAcceptButton = useHandler((value: string) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].variantExchangeAddOnAcceptButton = value;
      }),
    );
  });

  const handleVariantExchangeAddOnRejectButton = useHandler((value: string) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].variantExchangeAddOnRejectButton = value;
      }),
    );
  });

  const handleExchangeShipping = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].chargeCustomerForNewOrderShipping = value;
      }),
    );
  });

  const handleRefundPointsRedeemed = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].refundPointsRedeemedForItem = value;
      }),
    );
  });

  const handleDeductEarnedPoints = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].deductEarnedPointsFromRefund = value;
      }),
    );
  });

  const handleReimburseForFinalSale = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].reimburseForFinalSale = value;
      }),
    );
  });
  if (!isEnabled) {
    return (
      <Flex className={returnCss.optionCard} dir="column" gap="md">
        <Flex align="center" dir="row" justify="space-between">
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            {returnOptionMethod(optionType)}
          </Text>
          <RedoToggle
            disabled={!canEditReturnsSettings}
            setValue={handleToggle}
            size="sm"
            value={false}
          />
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex className={returnCss.optionCard} dir="column" gap="md">
      <Flex align="center" dir="row" justify="space-between">
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          {returnOptionMethod(optionType)}
        </Text>
        <RedoToggle
          disabled={!canEditReturnsSettings}
          setValue={handleToggle}
          size="sm"
          value
        />
      </Flex>
      <AdjustmentElement
        adjustmentFlat={option?.adjustmentFlat || "0"}
        adjustmentPercent={option?.adjustmentPercent || "0"}
        canEditReturnsSettings={canEditReturnsSettings}
        handleAdjustmentFlat={handleAdjustmentFlat}
        handleAdjustmentPercent={handleAdjustmentPercent}
      />
      <RedoCollapseSubsection title="Advanced options">
        <RedoToggle
          description="Customer does not send item back."
          disabled={!canEditReturnsSettings}
          label="Green return"
          setValue={handleGreenReturn}
          size="sm"
          value={!option!.ship}
        />
        <RedoToggle
          description="Merchant pays for labels not covered by Redo."
          disabled={!canEditReturnsSettings}
          label="Safety net"
          setValue={handleSafetyNet}
          size="sm"
          value={option!.labelPayer === Payer.MERCHANT}
        />
        <RedoToggle
          description="Merchant will handle the shipping label"
          disabled={!canEditReturnsSettings}
          label="Manual Label"
          setValue={handleManualLabel}
          size="sm"
          value={option!.manual_label ?? false}
        />
        <RedoToggle
          description="Controls whether merchants are reimbursed for final sale returns processed through this option"
          disabled={!canEditReturnsSettings}
          label="Reimburse for final sale"
          setValue={handleReimburseForFinalSale}
          size="sm"
          value={option!.reimburseForFinalSale ?? true}
        />
        {optionType === ReturnOptionMethod.EXCHANGE && (
          <>
            <Flex dir="column" gap="sm">
              <Flex dir="column" gap="xxs">
                <Text fontSize="sm" fontWeight="medium" textColor="primary">
                  Allow exchange types
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  Select what types of exchanges to allow.
                </Text>
                {option!.exchangeTypes!.advanced &&
                  team.settings.exchanges.advancedExchanges ===
                    AdvancedExchanges.DISABLED && (
                    <Text fontSize="xs" textColor="secondary">
                      ⚠ Advanced exchanges are disabled globally in your
                      Exchanges settings.
                    </Text>
                  )}
              </Flex>
              <CheckboxGroup>
                <Checkbox
                  disabled={!canEditReturnsSettings}
                  onChange={handleExchangeTypeSameItem}
                  value={option!.exchangeTypes!.sameItem}
                >
                  Same item
                </Checkbox>
                <Checkbox
                  disabled={!canEditReturnsSettings}
                  onChange={handleExchangeTypeVariant}
                  value={option!.exchangeTypes!.variant}
                >
                  Variant items
                </Checkbox>
                <Checkbox
                  disabled={!canEditReturnsSettings}
                  onChange={handleExchangeTypeExchangeGroups}
                  value={option!.exchangeTypes!.exchangeGroups}
                >
                  Exchange groups
                </Checkbox>
                <Checkbox
                  disabled={!canEditReturnsSettings}
                  onChange={handleExchangeTypeAdvanced}
                  value={option!.exchangeTypes!.advanced}
                >
                  Any item
                </Checkbox>
              </CheckboxGroup>
            </Flex>
            <RedoToggle
              description="Offer the customer an add-on product if they do variant or same-item exchange. The customer will be charged the cost of the product."
              disabled={!canEditReturnsSettings}
              label="Variant exchange add-on"
              setValue={(value) => {
                const index = getIndex();
                if (index === -1) return;
                setState((state) =>
                  produce(state, (state) => {
                    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                    state.options[index].variantExchangeAddOn = value;
                  }),
                );
              }}
              size="sm"
              value={option!.variantExchangeAddOn ?? false}
            />
            {option!.variantExchangeAddOn && (
              <Flex dir="column" gap="lg">
                <ProductAutocomplete
                  label="Product to add to exchange"
                  productsState={productsState}
                  setValue={handleVariantExchangeVariantId}
                  value={selectedProduct}
                />
                <RedoTextInput
                  label="Title text"
                  setValue={handleVariantExchangeAddOnTitle}
                  value={option?.variantExchangeAddOnTitle ?? ""}
                />
                <Flex dir="row" gap="md">
                  <RedoTextInput
                    fullWidth
                    label="Accept button text"
                    setValue={handleVariantExchangeAddOnAcceptButton}
                    value={option?.variantExchangeAddOnAcceptButton ?? ""}
                  />
                  <RedoTextInput
                    fullWidth
                    label="Reject button text"
                    setValue={handleVariantExchangeAddOnRejectButton}
                    value={option?.variantExchangeAddOnRejectButton ?? ""}
                  />
                </Flex>
              </Flex>
            )}
            <RedoToggle
              description="If enabled, customer will be charged for outgoing shipping of exchange orders."
              disabled={!canEditReturnsSettings}
              label="Charge customer for new order shipping"
              setValue={handleExchangeShipping}
              size="sm"
              value={option!.chargeCustomerForNewOrderShipping ?? false}
            />
          </>
        )}
      </RedoCollapseSubsection>
      {team.settings.marketing?.loyalty?.enabled && (
        <CollapseSubsection title="Loyalty options">
          <RedoToggle
            description="Refund points that were redeemed for this specific item"
            disabled={!canEditReturnsSettings}
            label="Refund points redeemed for this item"
            setValue={handleRefundPointsRedeemed}
            size="sm"
            value={option!.refundPointsRedeemedForItem ?? false}
          />
          <RedoToggle
            description="Deduct loyalty points earned from this purchase from the refund amount"
            disabled={!canEditReturnsSettings}
            label="Deduct earned points from refund"
            setValue={handleDeductEarnedPoints}
            size="sm"
            value={option!.deductEarnedPointsFromRefund ?? false}
          />
        </CollapseSubsection>
      )}
    </Flex>
  );
});
